import { cn } from '@/shadcn-ui/utils';
import { events, Viewer } from '@photo-sphere-viewer/core';
import '@photo-sphere-viewer/core/index.css';
import { useEffect, useRef, useState } from 'react';
import { useBreakpoint } from '../hooks/use-breakpoint';
import { logger } from '~/logger/logger';

interface PhotoSphereProps {
  url: string;
  className?: string;
  /** Intended to communicate when viewer enters fullscreen */
  fullscreenEventDelegate?: (isFullscreen: boolean) => void;
  /** Flag to indicate if viewer should be initialized in fullscreen mode. Intended to be used for mobile. */
  startInFullscreen?: boolean;
  /** Flag to indicate if pill overlay should be shown at all. */
  enablePillOverlay?: boolean;
}

export default function PhotoSphere({
  url,
  className,
  fullscreenEventDelegate,
  startInFullscreen = false,
  enablePillOverlay = false,
}: PhotoSphereProps) {
  const breakpoint = useBreakpoint();
  const viewerContainerRef = useRef(null);
  const [viewerInitialized, setViewerInitialized] = useState(false);
  const [showPillOverlay, setShowPillOverlay] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const dismissPillOverlay = () => setShowPillOverlay(false);
  const displayPillOverlay = () => setShowPillOverlay(true);

  useEffect(() => {
    let viewer: Viewer | null = null;
    const handleFullscreen = (e: events.FullscreenEvent) => {
      if (e.type === 'fullscreen') {
        fullscreenEventDelegate?.(e.fullscreenEnabled);
        setIsFullscreen(e.fullscreenEnabled);
      }
    };

    if (viewerContainerRef.current && !viewerInitialized) {
      /** Docs to config: https://photo-sphere-viewer.js.org/guide/config.html */
      viewer = new Viewer({
        container: viewerContainerRef.current,
        panorama: url,
        navbar: ['zoom', 'move', 'fullscreen'],
        sphereCorrection: { pan: -0.5, tilt: 0, roll: 0 }, // Position viewport towards steering wheel
      });

      setViewerInitialized(true);

      viewer.addEventListener(events.FullscreenEvent.type, handleFullscreen);

      try {
        // Only start the viewer in fullscreen mode for mobile devices.
        if (startInFullscreen && breakpoint !== 'desktop') {
          viewer.enterFullscreen();
          setIsFullscreen(true);
        }
      } catch (err) {
        // Fail silently and log the error. This can happen in a rare race condition where the viewer's container isn't set yet by the time we
        // command the viewer to enter fullscreen mode.
        logger.error('photoSphere - enterFullscreen', err);
      }
    }

    return () => {
      if (viewerContainerRef.current && viewerInitialized) {
        viewer?.removeEventListener(events.FullscreenEvent.type, handleFullscreen);
        viewerContainerRef.current = null;
      }
    };
  }, [breakpoint, fullscreenEventDelegate, startInFullscreen, url, viewerInitialized]);

  // Force the move and zoom buttons to be always be visible regardless of device type after the viewer has been initialized. By default,
  // Photo Sphere Viewer hides the buttons on devices with touch enabled.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (viewerInitialized) {
      const controlButtons = document.querySelectorAll(
        'div.psv-move-button, div.psv-zoom-button, div.psv-zoom-range'
      ) as NodeListOf<HTMLElement>;

      // On mobile, these buttons are given the style `display: none;` by default so we need to remove that to get them to show.
      if (controlButtons) {
        controlButtons.forEach((button) => {
          button.removeAttribute('style');
        });
      }
    }
  }, [viewerInitialized]);

  return (
    <div
      ref={viewerContainerRef}
      className={cn(
        'relative h-full w-full overflow-hidden md:rounded-[16px]',
        className
      )}
      onMouseEnter={dismissPillOverlay}
      onMouseLeave={displayPillOverlay}
      onTouchStart={dismissPillOverlay}
      onTouchEnd={displayPillOverlay}
    >
      {showPillOverlay && enablePillOverlay && !isFullscreen ? (
        <p className="absolute left-1/2 top-1/2 z-1 -translate-x-1/2 -translate-y-1/2 rounded-[32px] bg-flexcar-neutral100/90 px-[12px] py-[8px] font-sans-tight text-[10px] font-bold leading-[150%] tracking-[0.8px] text-flexcar-neutral20 group-hover:bg-flexcar-neutral100 group-hover:text-flexcar-neutral1">
          360º INTERIOR
        </p>
      ) : null}
    </div>
  );
}
